import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* Failure is a real HTTP status with a { code, message } body; there is no
   status field inside the body. Some codes carry no message. */
const errorText = (err, fallback) => {
  const data = err.response?.data;
  return data?.message || data?.code || fallback;
};

/* ============================
   THE MENU-ACCESS MATRIX
   GET /admin/menu answers { data: rows }, one row per screen path:
     { id, title, path, icon, sortOrder, category, roles: [1|2|3], isActive }
   The whole matrix, not one role's slice of it. A screen with no row has
   never been saved and is visible to every role by default.
============================ */
export const fetchMenuAccess = createAsyncThunk(
  "permission/fetchMenuAccess",
  async (_, thunkAPI) => {
    try {
      const res = await api.get("/admin/menu");
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Failed to load menu access"));
    }
  }
);

/* PUT /admin/menu { items: [{ title, path, icon, sortOrder, category, roles }] }
   Upserts by path and answers the saved matrix. Superadmin only; the server
   adds role 1 to every row, so the role doing the editing can never lock
   itself out. */
export const saveMenuAccess = createAsyncThunk(
  "permission/saveMenuAccess",
  async (items, thunkAPI) => {
    try {
      const res = await api.put("/admin/menu", { items });
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Failed to save menu access"));
    }
  }
);

const permissionSlice = createSlice({
  name: "permission",
  initialState: {
    rows: [],
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMenuAccess.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenuAccess.fulfilled, (state, action) => {
        state.loading = false;
        state.rows = action.payload || [];
      })
      .addCase(fetchMenuAccess.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(saveMenuAccess.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveMenuAccess.fulfilled, (state, action) => {
        state.saving = false;
        state.rows = action.payload || state.rows;
      })
      .addCase(saveMenuAccess.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export default permissionSlice.reducer;
